import { useRef, useState, type FormEvent } from 'react';
import { login, ApiError, ApiRequestError, type AppData } from '../api/client';
// Imported rather than referenced by path: Vite fingerprints it and serves it
// from /app/assets/, so it does not depend on SnappyMail's versioned theme URL
// (/snappymail/v/<version>/themes/...), which moves on every upstream bump.
import logo from '../assets/logo.png';
import { Button, Card, CardBody, Input } from '../ui';

/**
 * Staff addresses all live here, so the sign-in box only needs the part before
 * the @. The server does the same thing through login.default_domain; doing it
 * here too means sign-in does not silently depend on that config value.
 */
export const MAIL_DOMAIN = 'keystonemgmt.io';

/** "jack" -> "jack@keystonemgmt.io"; a full address is left exactly as typed. */
export function fullAddress(value: string): string {
	const trimmed = value.trim();
	return !trimmed || trimmed.includes('@') ? trimmed : `${trimmed}@${MAIL_DOMAIN}`;
}

/**
 * Two-step sign-in.
 *
 * The code field does not exist until the server says this account needs one,
 * which it only does once the password has already been accepted. People without
 * 2FA never see it, and nobody is asked for a code before they have typed
 * anything -- the complaint that started this rebuild.
 */
export function LoginScreen({ onSignedIn }: { onSignedIn: (data: AppData) => void }) {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [code, setCode] = useState('');
	const [needsCode, setNeedsCode] = useState(false);
	const [hint, setHint] = useState('');
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);
	const codeRef = useRef<HTMLInputElement>(null);

	async function submit(event: FormEvent) {
		event.preventDefault();
		if (busy) {
			return;
		}

		setBusy(true);
		setError('');

		try {
			const data = await login(fullAddress(email), password, needsCode ? code : undefined);
			onSignedIn(data);
		} catch (e: unknown) {
			if (e instanceof ApiRequestError && (e.needsTotp || e.badTotp)) {
				setNeedsCode(true);
				setCode('');
				setHint(
					e.badTotp
						? 'That code was not right. Try the next one your app shows.'
						: 'Enter the 6-digit code from your authenticator app.'
				);
				// Focus after the field has actually rendered.
				requestAnimationFrame(() => codeRef.current?.focus());
			} else {
				setError(describeLoginError(e));
			}
		} finally {
			setBusy(false);
		}
	}

	// The address and password are locked while the code is asked for, so without
	// this a typo in the address meant reloading the page to escape step two.
	function startOver() {
		setNeedsCode(false);
		setCode('');
		setHint('');
		setError('');
		setPassword('');
	}

	return (
		<div className="login">
			<div className="login__card">
				<img className="login__logo" src={logo} alt="Keystone Management" />
				<Card>
					<form onSubmit={submit}>
						<CardBody>
							{/*
							 * type="text", not "email": login.default_domain lets staff sign in
							 * with just "jack", and an email input refuses to submit anything
							 * without an @ -- the browser blocked those logins before the server
							 * ever saw them. inputMode still brings up the email keyboard.
							 */}
							<Input
								type="text"
								inputMode="email"
								name="email"
								value={email}
								onChange={event => setEmail(event.target.value)}
								placeholder="Your name, e.g. jack"
								aria-label="Email address"
								// Shows the domain that will be added, until someone types
								// their own @ -- then what they typed is used as-is.
								endContent={email.includes('@') ? undefined : `@${MAIL_DOMAIN}`}
								autoComplete="username"
								autoCapitalize="none"
								autoCorrect="off"
								size="lg"
								required
								disabled={busy || needsCode}
							/>
							<Input
								type="password"
								name="password"
								value={password}
								onChange={event => setPassword(event.target.value)}
								placeholder="Password"
								autoComplete="current-password"
								size="lg"
								required
								disabled={busy || needsCode}
							/>

							{needsCode && (
								<Input
									ref={codeRef}
									type="text"
									name="code"
									label="Verification code"
									description={hint}
									value={code}
									onChange={event => setCode(event.target.value)}
									placeholder="000000"
									inputMode="numeric"
									autoComplete="one-time-code"
									maxLength={12}
									size="lg"
									required
								/>
							)}

							{needsCode && (
								<p className="ks-muted" style={{ margin: 0, fontSize: 13 }}>
									Lost your phone? Enter one of the backup codes you saved when you set
									this up.
								</p>
							)}

							{error && <p className="ks-error">{error}</p>}

							<Button type="submit" size="lg" block loading={busy}>
								{needsCode ? 'Verify' : 'Sign in'}
							</Button>

							{needsCode && (
								<Button variant="ghost" onClick={startOver} disabled={busy}>
									Use a different account
								</Button>
							)}
						</CardBody>
					</form>
				</Card>
			</div>
		</div>
	);
}

/**
 * Says what actually went wrong. Everything used to read "email and password did
 * not match" -- including the server being down and an expired page token -- so
 * people with the right password were told it was wrong and kept retyping it.
 */
function describeLoginError(e: unknown): string {
	if (e instanceof ApiRequestError) {
		switch (e.code) {
			case ApiError.AuthError:
				return 'That email address and password did not match.';
			case ApiError.InvalidToken:
				return 'This page had gone stale. Reload it and try again.';
			case ApiError.ConnectionError:
				return 'Could not reach the mail server. Please try again in a moment.';
		}
		if (e.code >= 400 && e.code < 600) {
			return 'The mail server did not respond properly. Please try again in a moment.';
		}
	}
	if (e instanceof TypeError) {
		// fetch() rejects with a TypeError when the network itself fails.
		return 'You appear to be offline. Check your connection and try again.';
	}
	return 'Sign in failed. Please try again.';
}
